import { useMemo } from "react";
import type { ReleaseDefinition } from "@legal-500/redis-db-manager-types";
import { usePublications } from "../../../hooks/usePublications.js";
import { isCustomRelease } from "./releaseDefinitionUtils.js";
import styles from "./DbManager.module.css";

type SlotReleaseDetailsProps = {
  releaseDefinition: ReleaseDefinition | undefined;
  projectionVersion: string | undefined;
};

const PUBLICATIONS_STATUS_FILTER = "Active,Researching";

export function SlotReleaseDetails({
  releaseDefinition,
  projectionVersion,
}: SlotReleaseDetailsProps) {
  if (!releaseDefinition || !isCustomRelease(releaseDefinition)) {
    return <p className={styles.releaseSectionEmpty}>No custom release applied (default sync).</p>;
  }

  return (
    <div className={styles.releaseSection}>
      <ReleaseOverrideList releaseDefinition={releaseDefinition} />
      {projectionVersion && (
        <div className={styles.releaseMetaRow}>
          <strong>Projection version:</strong> <code>{projectionVersion}</code>
        </div>
      )}
    </div>
  );
}

function ReleaseOverrideList({ releaseDefinition }: { releaseDefinition: ReleaseDefinition }) {
  const { data: publicationsData } = usePublications({ status: PUBLICATIONS_STATUS_FILTER });

  const publicationsById = useMemo(() => {
    const map = new Map<number, string>();
    for (const publication of publicationsData?.publications ?? []) {
      map.set(publication.id, `${publication.publication} (${publication.edition})`);
    }
    return map;
  }, [publicationsData]);

  return (
    <>
      {releaseDefinition.publications.length > 0 && (
        <ul className={styles.releasePublicationList}>
          {releaseDefinition.publications.map(override => (
            <li key={override.publicationId}>
              {renderPublicationLabel(override.publicationId, publicationsById)} — statuses:{" "}
              {override.postStatuses.join(", ")}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.releaseMetaRow}>
        <strong>Demotion policy:</strong> {releaseDefinition.demotionPolicy}
      </div>
      {releaseDefinition.asAt && (
        <div className={styles.releaseMetaRow}>
          <strong>As-at date:</strong> {releaseDefinition.asAt}
        </div>
      )}
    </>
  );
}

function renderPublicationLabel(
  publicationId: number,
  publicationsById: Map<number, string>,
): string {
  const name = publicationsById.get(publicationId);
  if (name) {
    return `${name} (#${publicationId})`;
  }
  return `Publication #${publicationId}`;
}
